import React from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import DOMPurify from "dompurify";
import marked from "marked";
import Typography from "@material-ui/core/Typography";
import AvatarView from "../../Common/AvatarView";
import ParticipantDetail from "../ParticipantList/ParticipantDetail";
import { downloadFile, isFileServiceUrl } from "../../../helpers/downloadFile";

const linkRenderer = new marked.Renderer();

linkRenderer.link = (href, title, text) => {
  title = title ? title : href;
  text = text ? text : href;

  return `<a href='${href}' title='${title}'>${text}</a>`;
};

const styles = (theme) => ({
  selfRoot: {
    backgroundColor: "#3289ef",
    border: "solid 1px #eaedf0",
    borderRadius: 15,
    display: "flex",
    padding: theme.spacing(1.5),
    flexGrow: 1,
  },
  remoteRoot: {
    backgroundColor: "#f7f8fd",
    border: "solid 1px #eaedf0",
    borderRadius: 15,
    display: "flex",
    padding: theme.spacing(1.5),
    flexGrow: 1,
  },
  selfMessage: {
    display: "flex",
    marginLeft: "auto",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  remoteMessage: {
    display: "flex",
    marginRight: "auto",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  text: {
    lineHeight: 1.5,
    fontSize: "0.8rem",
    fontWeight: 300,
    "& p": {
      margin: 0,
    },
  },
  caption: {
    flexGrow: 1,
    fontWeight: "bold",
  },
  selfContent: {
    width: "100%",
    color: "#fff",
  },
  remoteContent: {
    width: "100%",
    color: "#8790a6",
  },
  avatar: {
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  messageHeader: { display: "flex" },
  messageTime: {},
});

const Message = (props) => {
  const { self, peerId, picture, text, time, name, classes } = props;
  const displayName = useSelector((state) => state.settings.displayName);
  const peer = useSelector((state) => state.peers[peerId]);
  const [openUserDetail, setOpenUserDetail] = React.useState(false);

  let color = "#8790a6";
  if (self) {
    color = "#ffffff";
  }
  if (peer && peer.color) {
    color = peer.color;
  }

  return (
    <div
      className={classnames(self ? classes.selfMessage : classes.remoteMessage)}
    >
      {!self && (
        <AvatarView
          alt={name}
          className={classes.avatar}
          id={peerId}
          displayName={name}
        />
      )}
      <div className={classnames(self ? classes.selfRoot : classes.remoteRoot)}>
        <div className={self ? classes.selfContent : classes.remoteContent}>
          <div className={classes.messageHeader}>
            <Typography
              variant="caption"
              className={classes.caption}
              style={{ color: color, cursor: "pointer" }}
              onClick={() => {
                if (peerId.length > 8) {
                  //  setSelectedParticipant(peerId)
                  setOpenUserDetail(true);
                }
              }}
            >
              {name}
            </Typography>
            <Typography variant="caption" className={classes.messageTime}>
              {time}
            </Typography>
          </div>

          <Typography
            className={classes.text}
            variant="subtitle1"
            style={{ overflowWrap: "anywhere" }}
            onClick={(event) => {
              const anchor = event.target.closest && event.target.closest("a");
              if (!anchor || !anchor.href) return;

              event.preventDefault();
              event.stopPropagation();

              const name =
                (anchor.getAttribute("title") ||
                  anchor.textContent ||
                  "download").trim() || "download";

              if (isFileServiceUrl(anchor.href)) {
                downloadFile(anchor.href, name);
              } else {
                window.open(anchor.href, "_blank", "noopener,noreferrer");
              }
            }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                marked.parse(text, { renderer: linkRenderer }),
                {
                  ALLOWED_TAGS: ["a"],
                  ALLOWED_ATTR: ["href", "title"],
                }
              ),
            }}
          />
        </div>
      </div>
      {self && (
        <AvatarView
          alt={displayName}
          className={classes.avatar}
          id={peerId}
          displayName={displayName}
        />
      )}
      {openUserDetail && (
        <ParticipantDetail
          userId={peerId}
          onClose={() => {
            setOpenUserDetail(false);
          }}
        />
      )}
    </div>
  );
};

Message.propTypes = {
  self: PropTypes.bool,
  picture: PropTypes.string,
  text: PropTypes.string,
  time: PropTypes.object,
  name: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Message);
